<template>
  <KeyPoints
    v-if="content && keyPointsContent"
    class="advergaming-keypoints"
    :content="keyPointsContent"
  />
</template>

<script setup lang="ts">
import {
  computed,
  ComputedRef,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  watch,
} from 'vue'
import KeyPoints from '@/components/ui/KeyPoints.vue'
import type { KeyPoints as IKeypoints } from '@/components/ui/KeyPoints.vue'
import { useAdvergamingStore } from '@/stores/advergaming'
import gsap from 'gsap'
import { Picture } from '@/types'
import { useCycleList, UseCycleListReturn } from '@vueuse/core'
import { push } from '@/utils/tracking'

const props = defineProps({
  content: {
    type: Object as PropType<{
      carrots: {
        picture: Picture
        label: string // carottes
        text: string // récoltées sur cette page
      }
      deaths: {
        picture: Picture
        label: string // Piments
        text: string // ça pique!
      }
      time: {
        label: string // temps passé sur cette page
        text: string // c’est 450% de plus qu’une pub classique
      }
      clicks: {
        label: string // Clicks
        text: {
          count: number
          text: string
        }[]
      }
    }>,
    required: true,
  },
})

const store = useAdvergamingStore()

const AVERAGE_AD_WATCHTIME = 10_000 // in ms

const elapsed = ref(0)

const formatedElapsedTime = computed<string>(() => {
  const seconds = Math.floor(elapsed.value / 1000) % 60 // Get only the "seconds component" of elapsed time. Ex: 7000ms (1m10s) will return 10
  const minutes = Math.floor(elapsed.value / (1000 * 60)) // Get only the "minutes component" of elapsed time. Ex: 7000ms (1m10s) will return 1

  return `${minutes}’${seconds.toString().padStart(2, '0')}”`
})

const watchTimeDifferencePercent = computed<number>(
  () => ((elapsed.value - AVERAGE_AD_WATCHTIME) / AVERAGE_AD_WATCHTIME) * 100
)

// Create a new array describing the bounds of range (from, to) and the list of string (cycleList) to cycle through for said range
const clicksTextRanges: {
  from: number
  to: number
  cycleList: UseCycleListReturn<string>
}[] = (() => {
  // Temp fix to ensure retrocompatibility until api response is updated
  if (!Array.isArray(props.content.clicks.text)) {
    return [
      {
        from: -Infinity,
        to: Infinity,
        cycleList: useCycleList([props.content.clicks.text as string]),
      },
    ]
  }

  return (
    props.content.clicks.text
      // Sort from low to high
      .sort((a, b) => {
        return a.count - b.count
      })
      .map(({ count, text }, i, array) => ({
        from: count,
        to: i < array.length - 1 ? array[i + 1].count : Infinity, // Use next entry count for upper bound, if current entry is last, use +Infinity instead
        cycleList: useCycleList(text.split('|')),
      }))
  )
})()

// Current range best on store.clicks
const clicksText = computed(() => {
  const range = clicksTextRanges.find(
    ({ from, to }) => store.clicks >= from && store.clicks < to
  )

  return range
})

// At each click, update current cycleList.
// Note that when entering a new range next will be called so the first text we'll see for each range is actually the second entry.
watch(
  () => store.clicks,
  () => {
    clicksText.value?.cycleList.next()
  }
)

const keyPointsContent: ComputedRef<IKeypoints> = computed(() => ({
  items: [
    {
      picture: props.content?.carrots.picture,
      label: '',
      value: store.carrots.toString(),
      subtitle: props.content?.carrots.label,
      description: props.content?.carrots.text,
    },
    {
      picture: props.content?.deaths.picture,
      label: '',
      value: store.deaths.toString(),
      subtitle: props.content?.deaths.label,
      description: props.content?.deaths.text,
    },
    {
      label: '',
      value: formatedElapsedTime.value,
      subtitle: props.content?.time.label,
      description: props.content?.time.text.replace(
        '#',
        Math.round(watchTimeDifferencePercent.value).toString()
      ),
    },
    {
      label: '',
      value: store.clicks.toString(),
      subtitle: props.content?.clicks.label,
      description: clicksText.value?.cycleList.state.value ?? '',
    },
  ],
}))

const onTick: gsap.TickerCallback = (_time, delta) => {
  elapsed.value += delta
}

const onClick = () => {
  store.clicks += 1
}

onMounted(() => {
  store.$reset()

  gsap.ticker.add(onTick)
  window.addEventListener('click', onClick)

  window.addEventListener('pagehide', pushGTMData)
  window.addEventListener('visibilitychange', pushGTMData)
  window.addEventListener('beforeunload', pushGTMData)
})

onBeforeUnmount(() => {
  pushGTMData()

  gsap.ticker.remove(onTick)
  window.removeEventListener('click', onClick)
  removeGTMRelatedEvents()
})

let GTMEventFired = false

/**
 * In order not to send too much event, we want to send an event with multiple values at the end of the session.
 * To make sure that the event is actually sent, we use multiple events (onBeforeUnmount, pagehide, visibilitychange, beforeunload).
 * We also use a flag to avoid submitting the event multiple times.
 */
const pushGTMData = () => {
  if (GTMEventFired) {
    return
  }

  push({
    event: 'advergaming',
    carrots: store.carrots,
    deaths: store.deaths,
    carrotSurfersDuration: store.duration.carrotSurfer,
    whacACarrotDuration: store.duration.whacACarrot,
    carrotJumpDuration: store.duration.carrotJump,
    interactions: store.clicks,
  })

  GTMEventFired = true

  removeGTMRelatedEvents()
}

/**
 * Remove the events trigger in place to trigger the GTM event
 */
const removeGTMRelatedEvents = () => {
  window.removeEventListener('pagehide', pushGTMData)
  window.removeEventListener('visibilitychange', pushGTMData)
  window.removeEventListener('beforeunload', pushGTMData)
}
</script>

<style lang="scss" scoped>
.advergaming-keypoints {
  :deep() {
    user-select: none;

    .key-points-list {
      @include mq($from: s) {
        display: grid;
        grid-template-columns: 1fr 1fr;
      }

      @include mq($from: l) {
        grid-template-rows: 1fr 1fr;
        grid-template-columns: 1fr 1fr 1fr;
      }
    }

    .key-points__item__title__description {
      font-variant-numeric: tabular-nums;
    }

    .key-points__item {
      padding-block: 4rem;
    }

    .key-points__item:nth-child(1) {
      @include mq($from: s, $until: l) {
        border-bottom: 1px solid var(--c-foreground);
      }

      @include mq($from: l) {
        grid-row: 1 / 3;
      }
    }

    .key-points__item:nth-child(2) {
      @include mq($from: s, $until: l) {
        border-bottom: 1px solid var(--c-foreground);
        border-right-width: 0;
      }

      @include mq($from: l) {
        grid-row: 1 / 3;
      }
    }

    .key-points__item:nth-child(3) {
      @include mq($from: l) {
        grid-row: 1 / 2;
        border-bottom: 1px solid var(--c-foreground);
        border-right-width: 0;
      }
    }

    .key-points__item:nth-child(4) {
      @include mq($from: l) {
        grid-row: 2 / 3;
        grid-column: 3 / 4;
      }
    }

    .section-label {
      text-align: center;
    }

    @include mq($from: l) {
      .key-points__item {
        min-height: 19.5rem;
      }
    }
  }
}
</style>
